import { useEffect, useRef, useState } from "react";
import axios from "axios";
import SupportPercentChoice from "./SupportPercentChoice";
import PupilDecisionSupport from "./PupilDecisionSupport";

const API = process.env.REACT_APP_BACKEND_URL || "";
const EMPTY = { desired_focus: "", desired_level: null, desired_support_percent: null, concerns: "", intended_outcome: "" };
const QUESTIONS = ["desired_focus", "desired_level", "desired_support_percent", "concerns", "intended_outcome"];
const LABELS = {
  desired_focus: "What would you like to work on today?",
  desired_support_percent: "How much support would you like with your goal?",
  desired_level: "Keep all levels, or choose a specific challenge?",
  concerns: "Is anything worrying you about this lesson?",
  intended_outcome: "What would you like to feel clearer about by the end?",
};
const LEVELS = ["Calm, simple situations", "Routine decisions", "Busier, varied situations", "More complex situations"];
const LIMITS = { desired_focus: 240, concerns: 1000, intended_outcome: 500 };

export default function LessonPreparation({ lessonId, initialOpen = false, onSaved, onBusyChange, initialDraft, onDraftChange }) {
  const draftRef = useRef(initialDraft);
  draftRef.current = initialDraft;
  const [open, setOpen] = useState(initialOpen);
  const [reload, setReload] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [step, setStep] = useState(0);
  const [resources, setResources] = useState(null);
  const [resourceSource, setResourceSource] = useState(null);
  const [resourceError, setResourceError] = useState("");
  const [resourceTopic, setResourceTopic] = useState("");
  const resourceRequest = useRef(0);
  const mounted = useRef(false);
  const submitting = useRef(false);
  const fieldRef = useRef(null);
  const endpoint = `${API}/api/run-my-day/pupil/lessons/${encodeURIComponent(lessonId)}/preparation`;
  useEffect(() => { if (initialOpen) setOpen(true); }, [initialOpen]);
  useEffect(() => { setResourceTopic(answers.desired_focus); }, [answers.desired_focus, lessonId]);
  useEffect(() => { resourceRequest.current += 1; setResources(null); setResourceError(""); }, [resourceTopic, lessonId]);

  const findVideo = async (internalDidNotHelp = false) => {
    setResourceError("");
    const goal = resourceTopic.trim();
    const requestId = ++resourceRequest.current;
    try {
      const { data } = await axios.get(`${endpoint}/resources`, { params: { goal, internal_did_not_help: internalDidNotHelp }, withCredentials: true });
      if (mounted.current && requestId === resourceRequest.current && data?.lesson_id === lessonId && data?.goal === goal) { setResources(data.items || []); setResourceSource(data.source); }
    } catch { if (mounted.current && requestId === resourceRequest.current) setResourceError("I couldn’t check for a video. You can still choose your goal."); }
  };

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setStatus("loading");
    setError("");
    setSaved(null);
    setAnswers({ ...EMPTY });
    setStep(0);
    axios.get(endpoint, { withCredentials: true, signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (data?.lesson_id !== lessonId) throw new Error("Lesson changed");
      setSaved(data);
      setAnswers({ ...EMPTY, ...(data.pupil_proposal || draftRef.current?.answers || {}) });
      setStep(data.pupil_proposal ? 0 : draftRef.current?.step || 0);
      setStatus(data.pupil_proposal ? "saved" : "editing");
    }).catch(() => {
      if (controller.signal.aborted) return;
      setError("I couldn’t load this lesson’s preparation. Reload to try again.");
      setStatus("error");
    });
    return () => controller.abort();
  }, [open, reload, endpoint, lessonId]);

  useEffect(() => {
    if (status === "editing") fieldRef.current?.focus();
  }, [step, status]);

  const field = QUESTIONS[step];
  const valid = field === "desired_support_percent" ? true : field === "desired_level"
    ? answers[field] == null || [1, 2, 3, 4].includes(answers[field])
    : Boolean(answers[field]?.trim());
  const setAnswer = (value) => {
    const next = { ...answers, [field]: value };
    setAnswers(next); onDraftChange?.({ step, answers: next });
  };
  const changeStep = (next) => { setStep(next); onDraftChange?.({ step: next, answers }); };

  const save = async () => {
    if (submitting.current) return;
    submitting.current = true;
    onBusyChange?.(true);
    setStatus("saving");
    setError("");
    const proposal = { ...answers };
    for (const key of ["desired_focus", "concerns", "intended_outcome"]) proposal[key] = proposal[key].trim();
    try {
      const { data } = await axios.put(endpoint, proposal, { withCredentials: true });
      if (!mounted.current) return;
      if (data?.lesson_id !== lessonId || !QUESTIONS.every((key) => (data?.pupil_proposal?.[key] ?? null) === (proposal[key] ?? null))) {
        throw new Error("Save not confirmed");
      }
      setAnswers(proposal);
      setSaved(data);
      setStatus("saved");
      onSaved?.(data);
    } catch {
      if (!mounted.current) return;
      setError("I couldn’t confirm those answers were saved. Reload to check before trying again.");
      setStatus("error");
    } finally {
      submitting.current = false;
      if (mounted.current) onBusyChange?.(false);
    }
  };

  return (
    <section className="mt-3 border-t border-slate-200 pt-3 text-sm text-slate-900 dark:text-slate-100" data-testid={`lesson-preparation-${lessonId}`}>
      <button type="button" className="min-h-11 text-left font-bold text-blue-700 dark:text-blue-300" aria-expanded={open}
        disabled={status === "saving"} onClick={() => setOpen((value) => !value)}>
        {open ? "Close lesson preparation" : "Prepare for this lesson"}
      </button>
      {open && <div className="mt-2 space-y-3">
        {status === "loading" && <p role="status">Loading your preparation…</p>}
        {error && <p role="alert">{error}</p>}
        {status === "error" && <button type="button" className="min-h-11 underline" onClick={() => setReload((value) => value + 1)}>Reload preparation</button>}
        {status === "saved" && <>
          <p role="status" className="font-bold">Your goal and challenge are saved for this lesson.</p>
          <dl className="space-y-2 break-words">
            <div><dt className="font-semibold">Your goal</dt><dd>{answers.desired_focus}</dd></div>
            <div><dt className="font-semibold">Challenge</dt><dd>{answers.desired_level == null ? "Automatic — all levels 1–4" : `Level ${answers.desired_level} · ${LEVELS[answers.desired_level - 1]}`}</dd></div>
            <div><dt className="font-semibold">Support requested</dt><dd>{answers.desired_support_percent != null ? `${answers.desired_support_percent}%` : "Not stated"}</dd></div>
            <div><dt className="font-semibold">Concerns</dt><dd>{answers.concerns}</dd></div>
            <div><dt className="font-semibold">What you want from the lesson</dt><dd>{answers.intended_outcome}</dd></div>
          </dl>
          {saved?.instructor_plan_approved && <p>You and your instructor have an adjusted plan for this lesson. You can still discuss or change it together.</p>}
          {saved?.pupil_response_late && <p>Your choices have been added after the morning preparation time.</p>}
          <button type="button" className="min-h-11 underline" onClick={() => { setStep(0); setStatus("editing"); }}>Edit my answers</button>
        </>}
        {["editing", "saving"].includes(status) && <form onSubmit={(event) => {
          event.preventDefault();
          if (!valid || status === "saving") return;
          if (step < QUESTIONS.length - 1) changeStep(step + 1);
          else save();
        }} className="space-y-3">
          <p className="text-xs text-slate-500">Question {step + 1} of {QUESTIONS.length} · Your choices lead the lesson. You can adjust them together.</p>
          {field === "desired_focus" && saved?.suggested_programme_goal && <div className="rounded border border-blue-200 p-3">
            <p>Your programme suggests: <strong>{saved.suggested_programme_goal.goal}</strong></p>
            <p className="text-sm">{saved.suggested_programme_goal.stage_label} · The next learning item in your programme. You can use it, change it or choose another goal.</p>
            <button type="button" className="min-h-11 underline" onClick={() => setAnswer(saved.suggested_programme_goal.goal)}>Use this suggested goal</button>
          </div>}
          {field === "desired_support_percent" ? <SupportPercentChoice value={answers.desired_support_percent} onChange={setAnswer} label={LABELS[field]} disabled={status === "saving"} /> : field === "desired_level" ? <fieldset disabled={status === "saving"}>
            <legend className="font-semibold mb-2">{LABELS[field]}</legend>
            <p className="mb-2">Your journey includes levels 1–4 automatically. Choose one if you want a specific focus. You and your instructor can adjust the challenge together.</p>
            <div className="space-y-2">
              <button type="button" ref={fieldRef} aria-pressed={answers.desired_level == null} onClick={() => setAnswer(null)}
                className={`block w-full min-h-12 rounded-lg border px-3 py-2 text-left ${answers.desired_level == null ? "bg-blue-700 text-white" : "border-slate-300"}`}>
                Automatic — all levels 1–4
              </button>
              {LEVELS.map((label, index) => <button type="button" key={label}
                aria-pressed={answers.desired_level === index + 1} onClick={() => setAnswer(index + 1)}
                className={`block w-full min-h-12 rounded-lg border px-3 py-2 text-left ${answers.desired_level === index + 1 ? "bg-blue-700 text-white" : "border-slate-300"}`}>
                {index + 1} · {label}
              </button>)}
            </div>
          </fieldset> : <label className="block font-semibold">{LABELS[field]}
            <textarea ref={fieldRef} value={answers[field]} maxLength={LIMITS[field]} rows={3} required disabled={status === "saving"}
              onChange={(event) => setAnswer(event.target.value)} className="mt-2 w-full rounded-lg border border-slate-300 bg-white p-3 text-base text-slate-900" />
          </label>}
          {field === "concerns" && <button type="button" className="min-h-11 underline" onClick={() => setAnswer("No concerns today")}>No concerns today</button>}
          <div className="flex justify-between gap-3">
            {step > 0 && <button type="button" disabled={status === "saving"} className="min-h-11 px-3" onClick={() => changeStep(step - 1)}>Back</button>}
            <button type="submit" disabled={!valid || status === "saving"} className="min-h-11 rounded-lg bg-blue-700 px-4 py-2 font-bold text-white disabled:opacity-50">
              {status === "saving" ? "Saving…" : step === QUESTIONS.length - 1 ? "Save my choices" : "Continue"}
            </button>
          </div>
          <p className="text-xs text-slate-500">{onDraftChange ? "Drafts are private. Choose “Save my choices” to share your plan for this lesson." : "Answers are saved when you choose “Save my choices”."}</p>
        </form>}
        {["editing", "saved"].includes(status) && <PupilDecisionSupport key={lessonId} lessonId={lessonId} />}
        {["editing", "saved"].includes(status) && answers.desired_focus.trim() && <div>
          <label className="block">Would a video help with your goal or a particular question?
            <input className="block w-full rounded border p-2 text-slate-900" maxLength={240} value={resourceTopic}
              onChange={(event) => { resourceRequest.current += 1; setResourceTopic(event.target.value); }} />
          </label>
          <button type="button" className="min-h-11 underline" disabled={!resourceTopic.trim()} onClick={() => findVideo()}>Find an optional video</button>
          {resourceError && <p role="alert">{resourceError}</p>}
          {resources?.length === 0 && <p>No matching video was found. You can ask Karen about your goal or choose it without a video.</p>}
          {resources?.map((item) => <div key={item.id} className="mt-2">
            <a className="block min-h-11 underline" href={item.url} target="_blank" rel="noopener noreferrer">{item.title} · {item.publisher}{item.kind !== "internal_resource" ? " (external)" : ""}</a>
            <p>{item.reason}</p>
          </div>)}
          {resources && resourceSource === "internal" && <button type="button" className="min-h-11 underline" onClick={() => findVideo(true)}>This doesn’t answer my question — look externally</button>}
          {resources && resourceSource === "external" && <p>These external options are offered because matching internal material was unavailable or did not answer your question.</p>}
          {resources && <p className="text-xs">Watching is optional. It does not mark homework complete or prove understanding. You can discuss anything unclear with Karen or your instructor.</p>}
        </div>}
      </div>}
    </section>
  );
}

